import { format } from "@react-input/mask";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CardIcon from "../../../Assets/svgs/CardIcon";
import FilledInfoIcon from "../../../Assets/svgs/FilledInfoIcon";
import { Loader } from "../../../Common/Loader";
import { getUserData } from "../../../Common/utils";
import { ConfirmModel } from "../../../Model/ConfirmModel";
import { ErrorModel } from "../../../Model/ErrorModel";
import { SuccessModel } from "../../../Model/SuccessModel";
import { addPaymentMethod } from "../../../Redux/Slice/PaymentMethod/AddPaymentMethodSlice";
import { getPaymentMethodDetail } from "../../../Redux/Slice/PaymentMethod/GetPaymentMethodDetailSlice";

const AddEditPaymentMethod = ({ isEdit, editData, onClose }) => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const cardOptions = {
    mask: "____ ____ ____ ____",
    replacement: { _: /\d/ },
  };
  const [creditCardData, setCreditCardData] = useState({
    cardHolderName: "",
    cardNumber: "",
    expirationDate: "",
  });

  const [errorCreditCardData, setErrorCreditCardData] = useState({
    cardHolderNameError: "",
    cardNumberError: "",
    expirationDateError: "",
    cvcError: "",
  });

  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const [errorModal, setErrorModal] = useState({
    visible: false,
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [confirmModel, setConfirmModel] = useState(false);
  const currentUserData = useSelector((s) => s?.currentUser?.userData);
  const userData = currentUserData ? currentUserData : getUserData();

  const isDisabled = useMemo(() => {
    if (
      errorCreditCardData?.cardHolderNameError ||
      errorCreditCardData?.cardNumberError ||
      errorCreditCardData?.expirationDateError ||
      errorCreditCardData?.cvcError ||
      !creditCardData?.cardHolderName
    ) {
      return true;
    }
    return !hasChanged;
  }, [errorCreditCardData, creditCardData, hasChanged]);

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({
        visible: false,
        message: "",
      });
      if (typeof callback == "function") {
        callback();
      }
    }, 2000);
  };

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message: message,
    });

    setTimeout(() => {
      setSuccessModal({
        visible: false,
        message: "",
      });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const handleCloseConfirm = () => {
    setConfirmModel(false);
  };

  const getCardDetails = async () => {
    setIsLoading(true);
    try {
      const result = await dispatch(getPaymentMethodDetail(editData?.id));
      if (
        getPaymentMethodDetail.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        const data = result?.payload?.data;
        setCreditCardData({
          cardHolderName: data?.cardHolderName,
          cardNumber: format(data?.cardNumber, cardOptions),
          expirationDate: data?.cardExpiryDate,
        });
      }
    } catch (error) {}
    setIsLoading(false);
  };

  const handleChangeName = (e) => {
    const { name, value } = e.target;
    setCreditCardData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrorCreditCardData((error) => ({
      ...error,
      cardHolderNameError:
        value && value.length < 3 ? "Card Holder Name is too short" : "",
    }));

    if (!hasChanged) {
      setHasChanged(true);
    }
  };

  const handleChange = (e) => {
    if (e?.elementType == "cardNumber") {
      setErrorCreditCardData((error) => ({
        ...error,
        cardNumberError: e?.error?.message ?? "",
      }));
    } else if (e?.elementType == "cardExpiry") {
      setErrorCreditCardData((error) => ({
        ...error,
        expirationDateError: e?.error?.message ?? "",
      }));
    } else if (e?.elementType == "cardCvc") {
      setErrorCreditCardData((error) => ({
        ...error,
        cvcError: e?.error?.message ?? "",
      }));
    } 
    if (!hasChanged) {
      setHasChanged(true);
    }
  };

  const handleSubmit = async () => {
    setConfirmModel(false);
    setIsLoading(true);
    try {
      const cardElement = elements.getElement(CardNumberElement);
      const payload = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: creditCardData.cardHolderName,
        },
      });
      if (payload.error) {
        setIsLoading(false);
        showErrorMessage(payload.error.message);
        return;
      }
      const body = {
        stripePaymentMethodId: payload?.paymentMethod?.id,
        stripeCustomerId: userData?.customer?.stripeCustomerId,
      };
      const result = await dispatch(addPaymentMethod(body));
      if (
        addPaymentMethod.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        showSuccessMessage(
          `Credit Card ${isEdit ? "updated" : "saved"} successfully.`
        );
        onClose();
      } else {
        setIsLoading(false);
        showErrorMessage(
          result?.payload?.error?.message
            ? result?.payload?.error?.message
            : "Something went wrong."
        );
      }
    } catch (error) {
      setIsLoading(false);
      showErrorMessage("Something went wrong.");
    }
  };

  useEffect(() => {
    if (isEdit) {
      getCardDetails();
    }
  }, []);

  return (
    <div className="tabsContent__content tabsContent__content--selected">
      <div className="page__body">
        {isLoading ? <Loader /> : ""}
        {errorModal.visible ? <ErrorModel message={errorModal.message} /> : ""}
        {successModal.visible ? (
          <SuccessModel message={successModal.message} />
        ) : (
          ""
        )}
        {confirmModel ? (
          <ConfirmModel
            onAccept={handleSubmit}
            yes={"Yes"}
            no={"No"}
            onReject={handleCloseConfirm}
            title="Update Credit Card Details"
            message="Are you sure you want to update credit card details?"
          />
        ) : (
          ""
        )}
        <div className="details">
          <div className="form">
            <section className="form__section">
              <h1 className="form__section__title">
                {isEdit ? "Edit" : "Add"} New Credit Card
              </h1>
              <div className="formRow formRow__2">
                <div className="formCol">
                  <div className="form__Field">
                    <label for="cardHolderName" className="form__label">
                      Card Holder Name<span style={{ color: "red" }}> * </span>
                    </label>
                    <input
                      type="text"
                      id="cardHolderName"
                      name="cardHolderName"
                      value={creditCardData?.cardHolderName}
                      onChange={handleChangeName}
                      className="form__input form__input--sm"
                      placeholder="Card Holder Name"
                      maxLength={50}
                    />
                  </div>
                  {errorCreditCardData?.cardHolderNameError ? (
                    <div className="form__error">
                      {errorCreditCardData?.cardHolderNameError}
                    </div>
                  ) : null}
                </div>
                <div className="formCol">
                  <div className="form__Field">
                    <label for="cardNumber" className="form__label">
                      Card Number<span style={{ color: "red" }}> * </span>
                    </label>

                    <div className="form__groupField">
                      <CardNumberElement
                        className="form__input form__input--sm"
                        onChange={handleChange}
                      />
                      <div className="icon">
                        <CardIcon />
                      </div>
                    </div>
                  </div>
                  {errorCreditCardData?.cardNumberError ? (
                    <div className="form__error">
                      {errorCreditCardData?.cardNumberError}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="formRow formRow__2">
                <div className="formCol">
                  <div className="form__Field">
                    <label for="expirationDate" className="form__label">
                      Expiry Date<span style={{ color: "red" }}> * </span>
                    </label>
                    <div className="form__groupField">
                      <CardExpiryElement
                        onChange={handleChange}
                        className="form__input form__input--sm"
                      />
                    </div>
                  </div>
                  {errorCreditCardData?.expirationDateError ? (
                    <div className="form__error">
                      {errorCreditCardData?.expirationDateError}
                    </div>
                  ) : null}
                </div>
                <div className="formCol">
                  <div className="form__Field">
                    <label for="expirationDate" className="form__label">
                      CVC<span style={{ color: "red" }}> * </span>
                    </label>
                    <div className="form__groupField">
                      <CardCvcElement
                        onChange={handleChange}
                        className="form__input form__input--sm"
                      />
                    </div>
                  </div>
                  {errorCreditCardData?.cvcError ? (
                    <div className="form__error">
                      {errorCreditCardData?.cvcError}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="infoBox">
                <div className="icon">
                  <FilledInfoIcon />
                </div>
                <p className="para">
                  Our platform does not save any kind of payment information.
                </p>
              </div>
              <div className="btn__group">
                <button
                  className="btn btn__white"
                  onClick={onClose}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="btn btn__primary"
                  type="submit"
                  disabled={isDisabled}
                  onClick={isEdit ? () => setConfirmModel(true) : handleSubmit}
                >
                  {isEdit ? "Update" : "Add"} Card
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditPaymentMethod;
